import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";

const STATUSES = [["open", "Open"], ["in_progress", "In progress"], ["resolved", "Resolved"], ["closed", "Closed"]];
const COLOR = { open: "text-[#FF2E63]", in_progress: "text-[#FFB800]", resolved: "text-[#00F5D4]", closed: "text-slate-500" };
const sel = "h-9 rounded-md border border-white/10 bg-[#07090E] px-2 text-xs";
const when = (d) => new Date(d).toLocaleString();

const Ticket = ({ t, onChange }) => {
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const status = async (s) => {
    try { onChange((await api.post(`/admin/tickets/${t.id}/status`, { status: s })).data); toast.success("Status updated"); } catch (er) { toast.error(errMsg(er)); }
  };
  const reply = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/tickets/${t.id}/reply`, { message: msg });
      onChange(data.ticket); setMsg("");
      data.email === "sent" ? toast.success("Reply sent by email") : toast.warning(`Reply saved — email ${data.email}`);
    } catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };
  return (
    <div className="panel space-y-4 p-5" data-testid={`ticket-${t.number}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-widest text-slate-500">#{t.number} · {t.reason} · {when(t.created_at)}</div>
          <div className="mt-1 font-display text-lg font-bold">{t.subject}</div>
          <div className="text-sm text-slate-400">{t.name} · <a href={`mailto:${t.email}`} className="text-[#00F5D4]">{t.email}</a>{t.user_id && " · registered"}</div>
        </div>
        <select value={t.status} onChange={(e) => status(e.target.value)} className={`${sel} ${COLOR[t.status]}`} data-testid={`ticket-status-${t.number}`}>
          {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
      <p className="whitespace-pre-wrap rounded-lg bg-[#07090E] p-4 text-sm text-slate-300">{t.message}</p>
      {t.replies.map((r) => (
        <div key={r.id} className="ml-6 border-l-2 border-[#00F5D4] pl-4 text-sm" data-testid={`ticket-reply-${r.id}`}>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#00F5D4]">Your reply · {when(r.created_at)}</div>
          <p className="mt-1 whitespace-pre-wrap text-slate-300">{r.message}</p>
        </div>
      ))}
      <form onSubmit={reply} className="space-y-2">
        <Textarea required rows={3} placeholder="Write a reply — it will be emailed to the sender" value={msg} onChange={(e) => setMsg(e.target.value)} className="border-white/10 bg-[#07090E]" data-testid={`ticket-reply-input-${t.number}`} />
        <button className="btn-hot !py-2 text-sm" disabled={busy} data-testid={`ticket-reply-send-${t.number}`}>{busy ? "Sending…" : "Send reply"}</button>
      </form>
    </div>
  );
};

export const TicketsAdmin = () => {
  const [filter, setFilter] = useState("open");
  const [data, setData] = useState({ tickets: [], counts: {} });
  const load = () => api.get("/admin/tickets", { params: { status: filter } }).then((r) => setData(r.data));
  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps
  const update = (nt) => { if (filter && nt.status !== filter) load(); else setData({ ...data, tickets: data.tickets.map((t) => (t.id === nt.id ? nt : t)) }); };
  return (
    <div className="space-y-5" data-testid="admin-tickets">
      <div className="flex flex-wrap gap-2">
        {[...STATUSES, ["", "All"]].map(([v, l]) => (
          <button key={v || "all"} onClick={() => setFilter(v)} data-testid={`tickets-filter-${v || "all"}`}
            className={`rounded-full px-4 py-1.5 font-mono text-[11px] uppercase tracking-widest ${filter === v ? "bg-[#FF2E63] text-white" : "bg-white/5 text-slate-400"}`}>
            {l}{v && ` (${data.counts[v] ?? 0})`}
          </button>
        ))}
      </div>
      {data.tickets.length === 0 && <p className="text-sm text-slate-500" data-testid="tickets-empty">No tickets here.</p>}
      {data.tickets.map((t) => <Ticket key={t.id} t={t} onChange={update} />)}
    </div>
  );
};
